(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    //AMD
    define(['jquery',
            'underscore',
            'remoteLogger',
            'app/urls',
            'torso/modules/ServiceCell',
            'app/applicationUtils',
            'app/financial/billingConfigModel',
            'app/financial/payment-processor/gip/merchantAccount/gipMerchantAccountCollection'], factory);
  } else if (typeof exports === 'object') {
    // Node, CommonJS-like
    var _ = require('underscore');
    var $ = require('jquery');
    var remoteLogger = require('./../remoteLogger');
    var urls = require('./../urls');
    var TorsoServiceCell = require('backbone-torso/modules/ServiceCell');
    var applicationUtils = require('./../applicationUtils');
    var billingConfigModel = require('./../financial/billingConfigModel');
    var gipMerchantAccountCollection = require('./../financial/payment-processor/gip/merchantAccount/gipMerchantAccountCollection');
    module.exports = factory( $, _, remoteLogger, urls, TorsoServiceCell, applicationUtils, billingConfigModel,
                             gipMerchantAccountCollection);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function ($, _, remoteLogger, urls, TorsoServiceCell, applicationUtils, billingConfigModel,
                  gipMerchantAccountCollection) {
  'use strict';

  var PAYMENT_PROCESSOR_NAMES = billingConfigModel.PAYMENT_PROCESSOR_NAMES;

  var PROCESSOR_SCRIPT_MAP = {};
  PROCESSOR_SCRIPT_MAP[PAYMENT_PROCESSOR_NAMES.GIP_PROCESSOR] = 'https://ecommerce.merchantware.net/v1/CayanCheckoutPlus.js';
  PROCESSOR_SCRIPT_MAP[PAYMENT_PROCESSOR_NAMES.TEMPUS_PROCESSOR] = 'https://vecna.spectrumretailnet.com/pp?PAGE=JSENCRYPT';

  /**
   * Singleton service for managing credit card data encryption scripts from the different processors.
   *
   * @class creditCardEncryptionService
   * @static
   * @extends Torso.ServiceCell
   */
  return new (TorsoServiceCell.extend({

    /**
     * @method initialize
     */
    initialize: function() {
      _.bindAll(this, '_getScript', '_validateScriptDownload',
                      '_gipEncryptSuccess', '_gipEncryptError', '_gipEncryptCreditCardWithDefaultAccount');
      remoteLogger.init(urls.remoteLogUrl);
    },

    /**
     * @method encryptCreditCard
     * @param creditCardData {Object}
     *   @param creditCardData.cardNumber {String} the card number to encrypt.
     *   @param creditCardData.cardType {String} not part of the tempus spec, but used by the mock processor.
     *   @param creditCardData.expiration {String} the expiration date to encrypt.
     *   @param creditCardData.securityCode {String} the security code to encrypt.
     *   @param creditCardData.encryptedCardInfoHiddenInputId {String} (only used by tempus) id of the input that tempus
     *                                                                 can use to save the encrypted card data.
     *   @param creditCardData.$encryptedCreditCardResultHiddenInput {jQuery.Selector} (only used by tempus) the hidden
     *                                                                                 input that the encrypted card
     *                                                                                 data can be set and retrieved from.
     * @return {jQuery.Deferred.Promise} a promise that resolves to the encrypted card data or rejects with the error response.
     */
    encryptCreditCard: function(creditCardData) {
      var encryptedCreditCardPromise = $.Deferred().resolve('no processor defined').promise();
      if (billingConfigModel.isMockProcessor()) {
        encryptedCreditCardPromise = this._mockEncryptCreditCard(creditCardData);
      } else if (billingConfigModel.isGipProcessor()) {
        encryptedCreditCardPromise = this._gipEncryptCreditCard();
      } else if (billingConfigModel.isTempusProcessor()) {
        encryptedCreditCardPromise = this._tempusEncryptCreditCard(creditCardData);
      }
      return encryptedCreditCardPromise;
    },

    /**
     * Get the encryption script for the configured payment processor that gets used when encrypting the credit card data.
     *
     * @method getEncryptionScript
     */
    getEncryptionScript: function() {
      var startTime = new Date();
      billingConfigModel.loadOnce()
        .then(this._getScript, function() {
          remoteLogger.log({
            eventType: 'Failed to download billing configuration',
            duration: (new Date() - startTime).toString()
          });
        })
        .then(_.noop, this._getScript)
        .then(_.noop, function() {
          remoteLogger.log({
            eventType: 'Failed to download encryption script (second try)',
            scriptUrl: this._getScriptUrl(),
            duration: (new Date() - startTime).toString()
          });
        }.bind(this));
    },

    /**
     * Get the appropriate url to use for the current processor.
     *
     * @method _getScriptUrl
     * @return {String} the url to use to download the script for the currently configured processor.
     * @private
     */
    _getScriptUrl: function() {
      var paymentProcessorName = billingConfigModel.getPaymentProcessorName();
      return PROCESSOR_SCRIPT_MAP[paymentProcessorName];
    },

    /**
     * Downloads the encryption script; handles the successful case
     *
     * @method _getScript
     * @returns {Object} the deferred object, so calling code can deal with failure case
     * @private
     */
    _getScript: function() {
      var startTime = new Date();
      var useMockEncryption = applicationUtils.currentApplication.mockCreditCardEncryption;
      var paymentProcessorName = billingConfigModel.getPaymentProcessorName();

      var getScriptPromise = $.Deferred().resolve().promise();
      if (useMockEncryption || !paymentProcessorName || billingConfigModel.isMockProcessor()) {
        // use default mock setup
      } else if (billingConfigModel.isGipProcessor() || billingConfigModel.isTempusProcessor()) {
        // Tempus
        delete window.BuildXML;
        // GIP
        delete window.CayanCheckoutPlus;
        getScriptPromise = $.getScript(this._getScriptUrl());
      } else {
        remoteLogger.log({
          eventType: 'Payment processor ' + paymentProcessorName + ' is not supported by the frontend.  Using mock processor.',
          duration: (new Date() - startTime).toString()
        });
      }

      return getScriptPromise
        .then(this._validateScriptDownload);
    },

    /**
     * Validate that the script downloaded successfully.
     * @method _validateScriptDownload
     * @private
     */
    _validateScriptDownload: function() {
      var scriptDownloadSuccess = false;
      if (billingConfigModel.isGipProcessor()) {
        scriptDownloadSuccess = window.CayanCheckoutPlus
                                && _.isFunction(window.CayanCheckoutPlus.setWebApiKey)
                                && _.isFunction(window.CayanCheckoutPlus.createPaymentToken);
      } else if (billingConfigModel.isTempusProcessor()) {
        scriptDownloadSuccess = _.isFunction(window.BuildXML);
      } else if (billingConfigModel.isMockProcessor) {
        scriptDownloadSuccess = true;
      }
      this.set('encryptionScriptAvailable', scriptDownloadSuccess);
      if (!scriptDownloadSuccess) {
        throw new Error('Failed to successfully execute encryption script');
      }
    },

    /**
     * GIP encryption function that runs the Cayan scripts that will perform the action of encrypting the card data.
     * The card data is harvested from the dom using "data-cayan" attributes so there are no values to pass in here.
     *
     * @method _gipEncryptCreditCard
     * @return {jQuery.Deferred.Promise} a promise that resolves to the encrypted string.
     * @private
     */
    _gipEncryptCreditCard: function() {
      this._cardEncryptionDeferred = $.Deferred();
      gipMerchantAccountCollection.getOrFetchDefaultAccount()
        .then(this._gipEncryptCreditCardWithDefaultAccount)
        .then(null, this._cardEncryptionDeferred.reject);
      return this._cardEncryptionDeferred.promise();
    },

    /**
     * After fetching the default account, then proceed with the gip encrypt card process.
     *
     * @method _gipEncryptCreditCardWithDefaultAccount
     * @param defaultAccountModel {GipMerchantAccountModel} the default merchant account.
     * @throws error if the default account isn't found or the web key is not set.
     * @private
     */
    _gipEncryptCreditCardWithDefaultAccount: function(defaultAccountModel) {
      if (!defaultAccountModel) {
        throw 'no default account';
      }
      var cardNotPresentWebApiKey = defaultAccountModel.get('cardNotPresentWebApiKey');
      if (!cardNotPresentWebApiKey) {
        throw 'no card not present web api key defined for the default account';
      }
      window.CayanCheckoutPlus.setWebApiKey(cardNotPresentWebApiKey);
      window.CayanCheckoutPlus.createPaymentToken({
        success: this._gipEncryptSuccess,
        error: this._gipEncryptError
      });
    },

    /**
     * Handle successful async credit card encrypt response.
     * @param tokenResponse {Object} the response.
     *   @param tokenResponse.token {String} the one time token to use for this payment (OTT).
     * @private
     */
    _gipEncryptSuccess: function(tokenResponse) {
      this._cardEncryptionDeferred.resolve(tokenResponse.token);
    },

    /**
     * Handle failed async credit card encrypt response.
     * @param errorResponse {Object} the error response from GIP.
     * @private
     */
    _gipEncryptError: function(errorResponse) {
      this._cardEncryptionDeferred.reject(errorResponse);
    },

    /**
     * The call to Tempus's encryption library takes the card number, expiration date, CVV and generates XML block
     * contained the encrypted values and bunch of other stuff.
     * It then puts that block as the value into an element with an ID passed as the last parameter.
     * If any errors occur (parameters are missing, have incorrect format, not all scripts are loaded, public key not available, etc.)
     * then it puts the description of the error as the value of that field.
     *
     * @method _tempusEncryptCreditCard
     * @param creditCardData {Object}
     *   @param creditCardData.cardNumber {String} the card number to encrypt.
     *   @param creditCardData.expiration {String} the expiration date to encrypt.
     *   @param creditCardData.securityCode {String} the security code to encrypt.
     *   @param creditCardData.encryptedCardInfoHiddenInputId {String} id of the input that tempus can use to save the encrypted card data.
     *   @param creditCardData.$encryptedCreditCardResultHiddenInput {jQuery.Selector} the hidden input that the encrypted card data can be set and retrieved from.
     * @return {String} the tempus encrypted string.
     * @private
     */
    _tempusEncryptCreditCard: function(creditCardData) {
      var creditCardEncryptDeferred = $.Deferred();

      var buildSuccess = window.BuildXML(creditCardData.cardNumber,
                                         creditCardData.expiration,
                                         creditCardData.securityCode,
                                         creditCardData.encryptedCardInfoHiddenInputId);
      if (buildSuccess) {
        var encryptedCreditCardInputValue = creditCardData.$encryptedCreditCardResultHiddenInput.val();
        creditCardEncryptDeferred.resolve(encryptedCreditCardInputValue);
      } else {
        creditCardEncryptDeferred.reject('');
      }
      return creditCardEncryptDeferred.promise();
    },

    /**
     * Fake encryption function that validates the inputs and then generates a fake encrypted string.
     *
     * @method _mockEncryptCreditCard
     * @param creditCardData {Object}
     *   @param creditCardData.cardNumber {String} the card number to encrypt.
     *   @param creditCardData.cardType {String} not part of the tempus spec, but used by the mock processor.
     *   @param creditCardData.expiration {String} the expiration date to encrypt.
     *   @param creditCardData.securityCode {String} the security code to encrypt.
     * @return {String} the fake encrypted string... always:
     *         '{
     *            "description": "encrypted cc generated by design demo script"
     *            "cardNumberLast4": "<card number last 4>",
     *            "expiration": "<expiration>",
     *            "cardType": "<cardType>"
     *          }'
     * @private
     */
    _mockEncryptCreditCard: function(creditCardData) {
      creditCardData = creditCardData || {};
      if (!creditCardData.cardNumber) {
        throw new Error('Card number not provided when encrypting card data.');
      }
      if (!creditCardData.expiration) {
        throw new Error('Expiration date not provided when encrypting card data.');
      }
      if (!creditCardData.securityCode) {
        throw new Error('Card number not provided when encrypting card data.');
      }
      var result = {
        description: 'encrypted cc generated by design demo script',
        cardNumberLast4: creditCardData.cardNumber.substr(creditCardData.cardNumber.length - 4),
        expiration: creditCardData.expiration,
        cardType: creditCardData.cardType
      };
      var encryptedCreditCard = JSON.stringify(result);
      return $.Deferred().resolve(encryptedCreditCard).promise();
    },

    /**
     * @method isUsingMockProcessor
     * @return {Boolean} true if the encryption is setup with mock, false otherwise.
     */
    isUsingMockProcessor: function() {
      return billingConfigModel.isMockProcessor();
    },

    /**
     * @method isUsingGipProcessor
     * @return {Boolean} true if the encryption is setup with gip, false otherwise.
     */
    isUsingGipProcessor: function() {
      return billingConfigModel.isGipProcessor();
    },

    /**
     * @method isUsingTempusProcessor
     * @return {Boolean} true if the encryption is setup with tempus, false otherwise.
     */
    isUsingTempusProcessor: function() {
      return billingConfigModel.isTempusProcessor();
    },

    /**
     * @method isEncryptionScriptAvailable
     * @returns {Boolean} true if the encryption script has been successfully downloaded, false/undefined otherwise.
     */
    isEncryptionScriptAvailable: function() {
      return this.get('encryptionScriptAvailable');
    }
  }))();
}));
